// Đánh giá nền tảng khi đăng xuất (docs/KE_HOACH_DANH_GIA_NEN_TANG.md). Super Admin soạn bộ câu hỏi (chọn mẫu rồi sửa);
// mọi tài khoản trừ Super Admin bấm Đăng xuất trên web (cả khu Quản trị NPP) thì được hỏi — trả lời hoặc Bỏ qua rồi mới thoát.
// Đã trả lời thì `cooldownDays` ngày sau mới hỏi lại (0 = hỏi mọi lần); Bỏ qua không tính là đã trả lời.
// Luật thuần, không I/O — dùng chung cho apps/api, apps/admin-api, packages/finance và giao diện.

export const SURVEY_QUESTION_TYPES = ['stars', 'single', 'multi', 'scale', 'text'] as const
export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPES)[number]

export const SURVEY_TYPE_LABEL: Record<SurveyQuestionType, string> = {
  stars: 'Chấm sao',
  single: 'Chọn một',
  multi: 'Chọn nhiều',
  scale: 'Thang điểm 0–10',
  text: 'Trả lời bằng chữ',
}

export interface SurveyQuestion {
  /** Khoá câu trong `answers` — chữ + số, duy nhất trong bộ */
  id: string
  type: SurveyQuestionType
  label: string
  required: boolean
  /** Chọn một / chọn nhiều: các lựa chọn (lưu câu trả lời bằng chính chữ lựa chọn) */
  options?: string[]
}

export interface SurveyContent {
  title: string
  questions: SurveyQuestion[]
}

/** Chấm sao 1–5 · thang điểm 0–10 · chọn một: chữ lựa chọn · chọn nhiều: mảng chữ · chữ */
export type SurveyAnswer = number | string | string[]
export type SurveyAnswers = Record<string, SurveyAnswer>

export const SURVEY_TITLE_MAX = 80
export const SURVEY_LABEL_MAX = 200
export const SURVEY_OPTION_MAX = 80
export const SURVEY_OPTIONS_MIN = 2
export const SURVEY_OPTIONS_MAX = 10
export const SURVEY_QUESTIONS_MAX = 10
export const SURVEY_TEXT_MAX = 1000
export const SURVEY_COOLDOWN_DEFAULT = 30
export const SURVEY_COOLDOWN_MAX = 365
export const SCALE_MIN = 0
export const SCALE_MAX = 10
export const STARS_MAX = 5

const DAY_MS = 86_400_000
const ID_RE = /^[a-z0-9]{1,16}$/i

export type SurveyTemplateKey = 'stars' | 'nps' | 'choice' | 'open'

/** Mẫu có sẵn — chữ trung tính (không ghi tên nền tảng: người dùng đang thấy thương hiệu NPP của họ). */
export const SURVEY_TEMPLATES: ReadonlyArray<{ key: SurveyTemplateKey; label: string; content: SurveyContent }> = [
  {
    key: 'stars',
    label: 'Chấm sao',
    content: {
      title: 'Đánh giá ứng dụng',
      questions: [
        { id: 'q1', type: 'stars', label: 'Bạn hài lòng với ứng dụng đến đâu?', required: true },
        { id: 'q2', type: 'text', label: 'Góp ý thêm', required: false },
      ],
    },
  },
  {
    key: 'nps',
    label: 'Điểm giới thiệu',
    content: {
      title: 'Đánh giá ứng dụng',
      questions: [
        { id: 'q1', type: 'scale', label: 'Bạn có muốn giới thiệu ứng dụng cho người quen không?', required: true },
        { id: 'q2', type: 'text', label: 'Vì sao bạn chọn điểm này?', required: false },
      ],
    },
  },
  {
    key: 'choice',
    label: 'Trắc nghiệm',
    content: {
      title: 'Đánh giá ứng dụng',
      questions: [
        {
          id: 'q1',
          type: 'single',
          label: 'Bạn thấy ứng dụng thế nào?',
          required: true,
          options: ['Rất hài lòng', 'Hài lòng', 'Bình thường', 'Chưa hài lòng', 'Không hài lòng'],
        },
        {
          id: 'q2',
          type: 'multi',
          label: 'Bạn muốn cải thiện điều gì?',
          required: false,
          options: ['Tốc độ', 'Dễ dùng', 'Tạo đơn', 'Tiền và đối soát', 'Vận chuyển', 'Hỗ trợ'],
        },
        { id: 'q3', type: 'text', label: 'Góp ý thêm', required: false },
      ],
    },
  },
  {
    key: 'open',
    label: 'Câu hỏi tự do',
    content: {
      title: 'Đánh giá ứng dụng',
      questions: [{ id: 'q1', type: 'text', label: 'Bạn muốn nhắn gì cho chúng tôi?', required: true }],
    },
  },
]

export const DEFAULT_SURVEY_TEMPLATE: SurveyTemplateKey = 'stars'

export function surveyTemplate(key: string): SurveyContent | null {
  const t = SURVEY_TEMPLATES.find((x) => x.key === key)
  return t ? structuredClone(t.content) : null
}

/** Mã câu mới không trùng các câu đang có (q1, q2…). */
export function nextQuestionId(questions: ReadonlyArray<Pick<SurveyQuestion, 'id'>>): string {
  const used = new Set(questions.map((q) => q.id))
  for (let i = questions.length + 1; ; i++) if (!used.has(`q${i}`)) return `q${i}`
}

const hasOptions = (t: SurveyQuestionType) => t === 'single' || t === 'multi'

/** Bộ câu hỏi đã cắt khoảng trắng; câu không có lựa chọn thì bỏ `options`. Gọi trước surveyContentError. */
export function normalizeSurveyContent(c: SurveyContent): SurveyContent {
  return {
    title: String(c.title ?? '').trim(),
    questions: (c.questions ?? []).map((q) => ({
      id: String(q.id ?? '').trim(),
      type: q.type,
      label: String(q.label ?? '').trim(),
      required: !!q.required,
      ...(hasOptions(q.type) ? { options: (q.options ?? []).map((o) => String(o ?? '').trim()) } : {}),
    })),
  }
}

/** Lỗi đầu tiên của bộ câu hỏi (đã normalize) — null = hợp lệ. */
export function surveyContentError(c: SurveyContent): string | null {
  if (!c.title) return 'Nhập tiêu đề'
  if (c.title.length > SURVEY_TITLE_MAX) return `Tiêu đề tối đa ${SURVEY_TITLE_MAX} ký tự`
  if (!c.questions.length) return 'Cần ít nhất một câu hỏi'
  if (c.questions.length > SURVEY_QUESTIONS_MAX) return `Tối đa ${SURVEY_QUESTIONS_MAX} câu hỏi`
  const ids = new Set<string>()
  for (const [i, q] of c.questions.entries()) {
    const n = `Câu ${i + 1}`
    if (!ID_RE.test(q.id) || ids.has(q.id)) return `${n}: mã câu không hợp lệ`
    ids.add(q.id)
    if (!(SURVEY_QUESTION_TYPES as readonly string[]).includes(q.type)) return `${n}: loại câu không hợp lệ`
    if (!q.label) return `${n}: nhập câu hỏi`
    if (q.label.length > SURVEY_LABEL_MAX) return `${n}: câu hỏi tối đa ${SURVEY_LABEL_MAX} ký tự`
    if (hasOptions(q.type)) {
      const opts = q.options ?? []
      if (opts.length < SURVEY_OPTIONS_MIN) return `${n}: cần ít nhất ${SURVEY_OPTIONS_MIN} lựa chọn`
      if (opts.length > SURVEY_OPTIONS_MAX) return `${n}: tối đa ${SURVEY_OPTIONS_MAX} lựa chọn`
      if (opts.some((o) => !o)) return `${n}: có lựa chọn còn trống`
      if (opts.some((o) => o.length > SURVEY_OPTION_MAX)) return `${n}: lựa chọn tối đa ${SURVEY_OPTION_MAX} ký tự`
      if (new Set(opts.map((o) => o.toLocaleLowerCase('vi'))).size !== opts.length) return `${n}: có lựa chọn trùng nhau`
    }
  }
  return null
}

/** Câu đã có câu trả lời chưa (giao diện: tô nút Gửi; máy chủ: kiểm câu bắt buộc). */
export function isAnswered(q: SurveyQuestion, v: SurveyAnswer | undefined): boolean {
  if (v == null) return false
  if (q.type === 'multi') return Array.isArray(v) && v.length > 0
  if (q.type === 'text') return typeof v === 'string' && v.trim().length > 0
  return v !== ''
}

/**
 * Câu trả lời client gửi lên → chỉ giữ câu có trong bộ, đúng kiểu, đúng phạm vi. Câu bắt buộc thiếu → lỗi.
 * Câu không bắt buộc bỏ trống thì không có khoá trong kết quả.
 */
export function cleanSurveyAnswers(questions: readonly SurveyQuestion[], raw: unknown): { answers: SurveyAnswers } | { error: string } {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const out: SurveyAnswers = {}
  for (const [i, q] of questions.entries()) {
    const v = src[q.id]
    const n = `Câu ${i + 1}`
    let val: SurveyAnswer | undefined
    if (v == null || v === '') val = undefined
    else if (q.type === 'stars' || q.type === 'scale') {
      const [lo, hi] = q.type === 'stars' ? [1, STARS_MAX] : [SCALE_MIN, SCALE_MAX]
      if (typeof v !== 'number' || !Number.isInteger(v) || v < lo || v > hi) return { error: `${n}: điểm không hợp lệ` }
      val = v
    } else if (q.type === 'single') {
      if (typeof v !== 'string' || !(q.options ?? []).includes(v)) return { error: `${n}: lựa chọn không hợp lệ` }
      val = v
    } else if (q.type === 'multi') {
      if (!Array.isArray(v) || v.some((x) => typeof x !== 'string' || !(q.options ?? []).includes(x))) return { error: `${n}: lựa chọn không hợp lệ` }
      // Giữ thứ tự như trong bộ câu hỏi, bỏ trùng
      const picked = (q.options ?? []).filter((o) => (v as string[]).includes(o))
      val = picked.length ? picked : undefined
    } else {
      if (typeof v !== 'string') return { error: `${n}: câu trả lời không hợp lệ` }
      const t = v.trim()
      if (t.length > SURVEY_TEXT_MAX) return { error: `${n}: tối đa ${SURVEY_TEXT_MAX} ký tự` }
      val = t || undefined
    }
    if (val === undefined) {
      if (q.required) return { error: `${n}: chưa trả lời` }
      continue
    }
    out[q.id] = val
  }
  return { answers: out }
}

/** Điểm chính của một lượt trả lời — câu chấm sao / thang điểm ĐẦU TIÊN có trả lời (lọc, tính trung bình). */
export function surveyRating(questions: readonly SurveyQuestion[], answers: SurveyAnswers): { kind: 'stars' | 'scale'; value: number } | null {
  for (const q of questions) {
    if (q.type !== 'stars' && q.type !== 'scale') continue
    const v = answers[q.id]
    if (typeof v === 'number') return { kind: q.type, value: v }
  }
  return null
}

/** Có hỏi khi đăng xuất không: đang bật và (hỏi mọi lần, chưa từng trả lời, hoặc lần trả lời gần nhất đã đủ N ngày). */
export function shouldAskSurvey(s: { enabled: boolean; cooldownDays: number; lastAnsweredAt: Date | null; now: Date }): boolean {
  if (!s.enabled) return false
  if (s.cooldownDays <= 0 || !s.lastAnsweredAt) return true
  return s.now.getTime() - s.lastAnsweredAt.getTime() >= s.cooldownDays * DAY_MS
}

/** Chữ của một câu trả lời (bảng, Excel). */
export function surveyAnswerText(q: Pick<SurveyQuestion, 'type'>, v: SurveyAnswer | undefined): string {
  if (v == null) return ''
  if (q.type === 'stars') return `${v}/${STARS_MAX} sao`
  if (q.type === 'scale') return `${v}/${SCALE_MAX}`
  return Array.isArray(v) ? v.join(', ') : String(v)
}

/** Điểm giới thiệu (NPS): % chấm 9–10 trừ % chấm 0–6, làm tròn. null khi chưa có điểm nào. */
export function npsScore(values: readonly number[]): number | null {
  if (!values.length) return null
  const promoters = values.filter((v) => v >= 9).length
  const detractors = values.filter((v) => v <= 6).length
  return Math.round(((promoters - detractors) / values.length) * 100)
}

// ─── Người trả lời (chụp lại lúc trả lời) ─────────────────────────
export const SURVEY_ACCOUNT_TYPES = ['shop', 'ctv', 'agency', 'staff', 'npp_admin'] as const
export type SurveyAccountType = (typeof SURVEY_ACCOUNT_TYPES)[number]

export const SURVEY_ACCOUNT_LABEL: Record<SurveyAccountType, string> = {
  shop: 'Shop',
  ctv: 'CTV',
  agency: 'Đại lý',
  staff: 'Nhân viên',
  npp_admin: 'Quản trị NPP',
}

/** Nơi đăng xuất: hiện chỉ web thành viên (gồm khu NPP trên web) — trang quản trị chỉ Super Admin dùng. Chừa 'admin' cho
 *  app quản trị sau này. */
export const SURVEY_APPS = ['web', 'admin'] as const
export type SurveyApp = (typeof SURVEY_APPS)[number]
export const SURVEY_DEVICES = ['desktop', 'mobile'] as const
export type SurveyDevice = (typeof SURVEY_DEVICES)[number]
export const SURVEY_DEVICE_LABEL: Record<SurveyDevice, string> = { desktop: 'Máy tính', mobile: 'Điện thoại' }
